import BlockIcon from '@mui/icons-material/Block';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExploreOffIcon from '@mui/icons-material/ExploreOff';
import HomeIcon from '@mui/icons-material/Home';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import LockIcon from '@mui/icons-material/Lock';
import LockClockIcon from '@mui/icons-material/LockClock';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PhonelinkLockIcon from '@mui/icons-material/PhonelinkLock';
import RefreshIcon from '@mui/icons-material/Refresh';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import React from 'react';
import { ALLIANCE_APPS } from './alliance-apps';
import { CURRENT_APP } from './current-app';
import {
  SystemAction,
  SystemDateChip,
  SystemIdentity,
  SystemScreen,
} from './system-screen';

/**
 * Thin configs over SystemScreen, one per case, with the copy from the
 * Claude Design SystemScreen spec (SCREENS / SECTION). Apps pass in the
 * handlers and identity; nothing here reads auth state.
 */

const CONTRACTING_URL = ALLIANCE_APPS.contracting.url;

const signOutAction = (onSignOut: () => void): SystemAction => ({
  label: 'Sign out',
  icon: <LogoutIcon />,
  onClick: onSignOut,
});

const goToContracting: SystemAction = {
  label: 'Go to Contracting',
  href: CONTRACTING_URL,
  external: true,
};

const retryAction = (onRetry: () => void): SystemAction => ({
  label: 'Try again',
  icon: <RefreshIcon />,
  onClick: onRetry,
});

/** Signed-out-safe props shared by the gate screens */
interface GateScreenProps {
  identity?: SystemIdentity | null;
  onSignOut: () => void;
}

/** MFA isn't set up: send them to Contracting to set it up */
export function MfaRequiredScreen(props: GateScreenProps) {
  return (
    <SystemScreen
      layout="page"
      tone="info"
      icon={<PhonelinkLockIcon />}
      eyebrow="Security"
      title="Set up multi‑factor authentication"
      body={
        <p>
          Every Alliance account needs a second sign-in step. Sign in at{' '}
          <a href={CONTRACTING_URL} target="_blank" rel="noopener noreferrer">
            thealliancecontracting.com
          </a>{' '}
          and select your preferred method.
        </p>
      }
      identity={props.identity}
      primaryAction={goToContracting}
      secondaryAction={signOutAction(props.onSignOut)}
      help
    />
  );
}

/** Agent status Pending: limited access until they finish in Contracting */
export function AccountPendingScreen(props: GateScreenProps) {
  return (
    <SystemScreen
      layout="page"
      tone="warning"
      icon={<HourglassTopIcon />}
      title="Your account is pending"
      body={
        <p>
          Your Alliance account is currently pending. You will have limited
          access to our websites until you:
        </p>
      }
      bullets={['Log in to Alliance Contracting', 'Sign the Agent Agreement']}
      identity={props.identity}
      primaryAction={goToContracting}
      secondaryAction={signOutAction(props.onSignOut)}
      help
    />
  );
}

/** Agent status Inactive */
export function AccountInactiveScreen(props: GateScreenProps) {
  return (
    <SystemScreen
      layout="page"
      tone="warning"
      icon={<PauseCircleIcon />}
      title="Your account is inactive"
      body={
        <p>
          Your Alliance account is not able to be accessed at this time due to
          inactivity. Please contact your manager or the Help Desk.
        </p>
      }
      identity={props.identity}
      secondaryAction={signOutAction(props.onSignOut)}
      help="include"
    />
  );
}

/** /authorize returned 403 */
export function NotAuthorizedScreen(props: GateScreenProps) {
  return (
    <SystemScreen
      layout="page"
      tone="error"
      icon={<BlockIcon />}
      title="You don’t have access"
      body={
        <p>
          Your Alliance account is not able to be accessed at this time. Please
          contact your manager or the Help Desk.
        </p>
      }
      identity={props.identity}
      secondaryAction={signOutAction(props.onSignOut)}
      help="include"
    />
  );
}

/** Agent status Terminated: deliberately minimal */
export function AccessDeniedScreen(props: { onSignOut: () => void }) {
  return (
    <SystemScreen
      layout="page"
      tone="neutral"
      icon={<LockIcon />}
      title="Access has been denied."
      secondaryAction={signOutAction(props.onSignOut)}
      showLinks={false}
    />
  );
}

/** /authorize failed for another reason; shows the API message when there is one */
export function AuthFailScreen(
  props: GateScreenProps & {
    onRetry: () => void;
    serverMessage?: string | null;
  },
) {
  return (
    <SystemScreen
      layout="page"
      tone="error"
      icon={<CloudOffIcon />}
      title="We couldn’t load your account"
      body={
        <p>
          Something went wrong while loading your account. Try again, and if it
          keeps happening, contact the Help Desk.
        </p>
      }
      serverMessage={props.serverMessage}
      identity={props.identity}
      primaryAction={retryAction(props.onRetry)}
      secondaryAction={signOutAction(props.onSignOut)}
      help
    />
  );
}

/**
 * Maintenance for agents. The end time lives in the date chip so it can
 * change without rewriting copy.
 *
 * @param until - Formatted end time, e.g. "Saturday, Oct 4 · 6:00 AM EDT"
 * @param onSignOut - Omit when nobody is signed in
 */
export function MaintenanceScreen(props: {
  until: string | null;
  identity?: SystemIdentity | null;
  onSignOut?: () => void;
}) {
  return (
    <SystemScreen
      layout="page"
      tone="warning"
      icon={<EngineeringIcon />}
      eyebrow="IT maintenance update"
      title="We’ll be back soon"
      body={
        <p>
          We’re performing scheduled maintenance to improve your experience.
          {props.until ? ' Please check back on:' : ' Please check back soon.'}
        </p>
      }
      detail={
        props.until ? <SystemDateChip>{props.until}</SystemDateChip> : null
      }
      identity={props.identity}
      secondaryAction={props.onSignOut ? signOutAction(props.onSignOut) : null}
      help
    />
  );
}

/**
 * Render crash. Inline when the page failed but the shell didn't; page when
 * the shell itself failed. No error code is shown.
 */
export function CrashScreen(props: {
  layout: 'page' | 'inline';
  onRetry: () => void;
  onHome: () => void;
}) {
  return (
    <SystemScreen
      layout={props.layout}
      tone="error"
      icon={<ErrorOutlineIcon />}
      title="Something went wrong"
      body={
        <p>
          This page ran into a problem. We’re working on it and we’ll get it
          fixed as soon as we can.
        </p>
      }
      primaryAction={retryAction(props.onRetry)}
      secondaryAction={{
        label: 'Go home',
        icon: <HomeIcon />,
        onClick: props.onHome,
      }}
    />
  );
}

/** Unknown route, inline in the shell instead of redirecting home */
export function NotFoundScreen(props: { onHome: () => void }) {
  return (
    <SystemScreen
      layout="inline"
      tone="neutral"
      icon={<ExploreOffIcon />}
      eyebrow="Page not found"
      title="We couldn’t find that page"
      body={
        <p>
          The link may be out of date, or the page may have moved. If you were
          looking for another Alliance site, try one of these:
        </p>
      }
      appList
      primaryAction={{
        label: 'Go home',
        icon: <HomeIcon />,
        onClick: props.onHome,
      }}
    />
  );
}

/**
 * Session expired, full page (when the app can't render). Neutral, no
 * eyebrow, identity or Help Desk, so it can't be mistaken for MFA.
 */
export function SessionExpiredScreen(props: { onSignIn: () => void }) {
  return (
    <SystemScreen
      layout="page"
      tone="neutral"
      icon={<LockClockIcon />}
      title="Your session has expired"
      body={<p>Please sign in again.</p>}
      primaryAction={{
        label: 'Sign in',
        icon: <LoginIcon />,
        onClick: props.onSignIn,
      }}
    />
  );
}

/**
 * A page's data didn't load. Offline (their side, neutral) or server
 * trouble (ours, warning), picked by navigator.onLine.
 */
export function LoadFailedScreen(props: {
  layout: 'page' | 'inline';
  onRetry: () => void;
}) {
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;
  return (
    <SystemScreen
      layout={props.layout}
      tone={offline ? 'neutral' : 'warning'}
      icon={offline ? <WifiOffIcon /> : <CloudOffIcon />}
      eyebrow="Couldn’t load this page"
      title={
        offline
          ? 'You appear to be offline'
          : 'We’re having trouble reaching our servers'
      }
      body={
        <p>
          {offline
            ? 'Check your connection and try again.'
            : 'Please try again in a moment.'}
        </p>
      }
      primaryAction={retryAction(props.onRetry)}
    />
  );
}

/** The API refuses the whole app (University) */
export function AppNoAccessScreen(props: GateScreenProps) {
  const appFull = `Alliance ${CURRENT_APP.name}`;
  return (
    <SystemScreen
      layout="page"
      tone="info"
      icon={<NoAccountsIcon />}
      title={`No access to ${appFull}`}
      body={
        <p>
          Your account doesn’t currently have access to {appFull}. Contact your
          upline or the Help Desk if you think this is a mistake.
        </p>
      }
      identity={props.identity}
      secondaryAction={signOutAction(props.onSignOut)}
      help="include"
    />
  );
}

/** A lazy chunk failed to load after a deploy */
export function AppUpdatedScreen(props: { onReload?: () => void }) {
  return (
    <SystemScreen
      layout="inline"
      tone="info"
      icon={<SystemUpdateAltIcon />}
      title={`${CURRENT_APP.name} has been updated`}
      body={<p>Reload to get the latest version. You won’t lose your place.</p>}
      primaryAction={{
        label: 'Reload',
        icon: <RefreshIcon />,
        onClick: props.onReload || (() => window.location.reload()),
      }}
    />
  );
}

/**
 * One section (tab, drawer, panel) failed; the rest of the page stays put.
 *
 * @param onRetry - Reloads only that section
 * @param dense - Inside a drawer
 * @param message - The API message, when there is one
 * @param inShell - The page's surface (default true). ARC passes false on
 *   its light admin pages, since ARC is otherwise dark inside the shell
 */
export function SectionErrorScreen(props: {
  onRetry?: () => void;
  dense?: boolean;
  message?: string | null;
  inShell?: boolean;
}) {
  return (
    <SystemScreen
      layout="compact"
      inShell={props.inShell}
      tone="error"
      icon={<ErrorOutlineIcon />}
      title="This section didn’t load."
      serverMessage={props.message}
      dense={props.dense}
      primaryAction={props.onRetry ? retryAction(props.onRetry) : null}
    />
  );
}
